import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useClerkSession } from '@/hooks/useClerkSession';
import { useEffect, useMemo, useState } from 'react';
import { Surface } from '@/components/ui/Surface';
import { PublicLanding } from '@/components/PublicLanding';
import { PlayerPhoto } from '@/components/PlayerPhoto';
import { LeagueFormatSelector, type LeagueFormatType } from '@/components/LeagueFormatSelector';
import { useSharedPlayerData } from '@/hooks/useSharedPlayerData';
import { createSharedLeagueConfig } from '@/utils/sharedLeagueConfig';
import { boardInsightSummary, type ScoringFormat } from '@/utils/leagueInsights';
import {
  deskSeasonLine,
  homeBoardRows,
  peekRoster,
  rosterIdsFromMetadata,
} from '@/utils/homeDesk';
import type { Player } from '@/types/player';

const POSITIONS = ['All', 'QB', 'RB', 'WR', 'TE'] as const;

const actions = [
  {
    name: 'Grade a trade',
    detail: 'Drop names on each side. The board tells you who wins.',
    href: '/trade-calculator',
    accent: 'cyan' as const,
  },
  {
    name: 'Start a mock',
    detail: 'Sixteen rounds, with the next pick aimed at a real roster hole.',
    href: '/draft-assistant',
    accent: 'pink' as const,
  },
  {
    name: 'Bring your team',
    detail: 'Import a Sleeper roster and the desk starts using your actual players.',
    href: '/my-roster',
    accent: 'gold' as const,
  },
];

function readStoredFormat(): LeagueFormatType {
  try {
    const stored = localStorage.getItem('leagueFormat');
    return (stored === 'superflex' || stored === '2qb' || stored === 'bestball' || stored === 'dynasty'
      ? stored
      : 'standard') as LeagueFormatType;
  } catch {
    return 'standard';
  }
}

export const Home = () => {
  const { isLoaded, signedIn } = useClerkSession();

  if (!isLoaded) {
    return (
      <div className="relative min-h-screen overflow-hidden bg-ink" aria-busy="true">
        <div className="pointer-events-none absolute inset-0 app-glow" />
      </div>
    );
  }

  return signedIn ? <SignedInHome /> : <PublicLanding />;
};

const SignedInHome = () => {
  const { signedIn, user } = useClerkSession();
  const [leagueFormat, setLeagueFormat] = useState<LeagueFormatType>(readStoredFormat);
  const [scoringFormat, setScoringFormat] = useState<ScoringFormat>('ppr');
  const [position, setPosition] = useState<(typeof POSITIONS)[number]>('All');
  const [shown, setShown] = useState<Player[]>([]);

  const leagueConfig = useMemo(() => {
    return createSharedLeagueConfig({
      format: leagueFormat,
      scoringFormat,
      teamCount: 12,
      isSuperflex: leagueFormat === 'superflex' || leagueFormat === '2qb',
    });
  }, [leagueFormat, scoringFormat]);

  const { players, isLoading, error } = useSharedPlayerData({
    leagueConfig,
    scaleToTen: false,
    scoringFormat,
  });

  useEffect(() => {
    localStorage.setItem('leagueFormat', leagueFormat);
  }, [leagueFormat]);

  useEffect(() => {
    if (players.length > 0) setShown(players);
  }, [players]);

  const rows = useMemo(() => homeBoardRows(shown, position, 10), [shown, position]);
  const updating = isLoading && shown.length > 0;
  const seasonLine = deskSeasonLine();
  const insight = boardInsightSummary(leagueFormat, scoringFormat);
  const rosterPlayers = useMemo(() => {
    const ids = rosterIdsFromMetadata(
      user?.unsafeMetadata?.fantasyRosters,
      user?.unsafeMetadata?.activeRosterId
    );
    return peekRoster(shown, ids, 8);
  }, [user, shown]);

  return (
    <div className="relative min-h-screen overflow-hidden bg-ink">
      <div className="pointer-events-none absolute inset-0 app-glow" />
      <div className="pointer-events-none absolute inset-0 stadium-grid opacity-50" />
      <Helmet>
        <title>Fantasy Fiesta, fantasy football made simple</title>
        <meta name="description" content="Live fantasy football ranks, a trade desk, and a mock draft that follow your league format." />
        <link rel="canonical" href="https://fantasy-fiesta.com/" />
        <meta property="og:title" content="Fantasy Fiesta, your fantasy football decision desk" />
        <meta property="og:description" content="Your league settings, your roster, and every decision in one place." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://fantasy-fiesta.com/" />
        <meta property="og:image" content="https://fantasy-fiesta.com/images/og-share-v3.png" />
        <meta property="og:image:secure_url" content="https://fantasy-fiesta.com/images/og-share-v3.png" />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Fantasy Fiesta: Run your fantasy team without the tab chaos." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Fantasy Fiesta, your fantasy football decision desk" />
        <meta name="twitter:description" content="Your league settings, your roster, and every decision in one place." />
        <meta name="twitter:image" content="https://fantasy-fiesta.com/images/og-share-v3.png" />
        <meta name="twitter:image:alt" content="Fantasy Fiesta: Run your fantasy team without the tab chaos." />
      </Helmet>

      <div className="relative z-10 mx-auto max-w-7xl px-4 pb-16 pt-10 sm:px-6 sm:pb-20 sm:pt-14 lg:px-8">
        <section className="mb-10 text-center md:mb-12">
          <p className="mb-3 text-sm font-medium text-fiesta-yellow">
            {signedIn && user?.firstName ? `Hey, ${user.firstName}` : 'Your Sunday desk'}
          </p>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-white sm:text-6xl lg:text-7xl text-balance">
            The board is open
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-zinc-400 sm:text-lg">
            {seasonLine}
          </p>
        </section>

        {signedIn ? (
          <Surface className="mb-6 p-4 sm:p-5">
            {rosterPlayers.length > 0 ? (
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="font-medium text-white">Your roster</h2>
                  <p className="text-sm text-zinc-500">Tap through to the full team.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {rosterPlayers.map((player) => (
                    <PlayerPhoto
                      key={String(player.id)}
                      name={player.name}
                      position={player.position}
                      team={player.team}
                      sleeperId={player.id}
                      espnId={player.espn_id}
                      imageUrl={player.imageUrl}
                      className="h-10 w-10 rounded-full object-cover ring-1 ring-white/10"
                    />
                  ))}
                  <Link
                    to="/my-roster"
                    className="rounded-full border border-white/10 px-3 py-1.5 text-sm text-zinc-300 hover:border-white/20 hover:text-white"
                  >
                    Open roster
                  </Link>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-zinc-400">
                  Import a Sleeper team and trades can start from players you actually own.
                </p>
                <Link
                  to="/my-roster"
                  className="inline-flex items-center justify-center rounded-full bg-fiesta-yellow px-4 py-2 text-sm font-semibold text-ink"
                >
                  Import a roster
                </Link>
              </div>
            )}
          </Surface>
        ) : null}

        <Surface className="p-4 sm:p-6" glow="gold">
          <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="text-left">
              <h2 className="font-display text-2xl font-semibold text-white">Live board</h2>
              <p className="mt-1 max-w-xl text-sm text-zinc-400">{insight}</p>
              {updating ? (
                <p className="mt-1 text-xs text-fiesta-yellow">Updating for this format.</p>
              ) : null}
            </div>
            <div className="space-y-3">
              <div className="hidden lg:block">
                <LeagueFormatSelector
                  selectedFormat={leagueFormat}
                  onFormatChange={setLeagueFormat}
                  variant="pills"
                  className="justify-center lg:justify-end"
                />
              </div>
              <div className="lg:hidden">
                <LeagueFormatSelector
                  selectedFormat={leagueFormat}
                  onFormatChange={setLeagueFormat}
                  variant="dropdown"
                />
              </div>
              <div className="flex flex-wrap justify-center gap-2 lg:justify-end">
                {([
                  { id: 'ppr', label: 'PPR' },
                  { id: 'half-ppr', label: 'Half PPR' },
                  { id: 'standard', label: 'Standard' },
                ] as const).map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setScoringFormat(option.id)}
                    className={`rounded-full border px-3 py-1.5 text-sm ${
                      scoringFormat === option.id
                        ? 'border-fiesta-blue bg-fiesta-blue/15 text-fiesta-blue'
                        : 'border-white/10 bg-white/5 text-zinc-300 hover:border-white/20'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-4 flex flex-wrap gap-2">
            {POSITIONS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setPosition(option)}
                aria-pressed={position === option}
                className={`rounded-full border px-3 py-1.5 text-sm ${
                  position === option
                    ? 'border-fiesta-yellow bg-fiesta-yellow text-ink'
                    : 'border-white/10 bg-white/5 text-zinc-300 hover:border-white/20'
                }`}
              >
                {option}
              </button>
            ))}
          </div>

          {error && shown.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-zinc-300">Could not load the board.</p>
              <p className="mt-1 text-sm text-zinc-500">{error}</p>
              <Link to="/players" className="mt-4 inline-block text-sm text-fiesta-yellow hover:underline">
                Try the full rankings page
              </Link>
            </div>
          ) : shown.length === 0 ? (
            <div className="space-y-2" aria-busy="true" aria-label="Loading the board">
              {Array.from({ length: 8 }).map((_, index) => (
                <div key={index} className="h-14 animate-pulse rounded-xl bg-white/5" />
              ))}
            </div>
          ) : (
            <ol className="space-y-1">
              {rows.map((player) => (
                <li key={String(player.id)}>
                  <Link
                    to="/players"
                    className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-white/5"
                  >
                    <span className="w-8 shrink-0 text-sm tabular-nums text-zinc-500">
                      {player.rank ?? ''}
                    </span>
                    <PlayerPhoto
                      name={player.name}
                      position={player.position}
                      team={player.team}
                      sleeperId={player.id}
                      espnId={player.espn_id}
                      imageUrl={player.imageUrl}
                      className="h-10 w-10 rounded-full object-cover"
                    />
                    <span className="min-w-0 flex-1 text-left">
                      <span className="block truncate text-sm font-medium text-white sm:text-base">
                        {player.name}
                      </span>
                      <span className="block text-xs text-zinc-500">
                        {player.position === 'DEF' ? 'D/ST' : player.position} · {player.team}
                        {player.insight ? ` · ${player.insight}` : ''}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          )}

          <div className="mt-5 flex justify-center">
            <Link
              to="/players"
              className="rounded-full bg-fiesta-yellow px-5 py-2.5 text-sm font-semibold text-ink"
            >
              See the full board
            </Link>
          </div>
        </Surface>

        <section className="mt-8 grid gap-4 sm:grid-cols-3">
          {actions.map((action, index) => (
            <motion.div
              key={action.name}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.08 + index * 0.05 }}
            >
              <Link to={action.href} className="block h-full">
                <Surface className="h-full p-5" glow={action.accent}>
                  <h2 className="font-display text-xl font-semibold text-white">{action.name}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-400">{action.detail}</p>
                </Surface>
              </Link>
            </motion.div>
          ))}
        </section>
      </div>
    </div>
  );
};
